import React from 'react';
import Image from 'next/image';
import { cn } from '@/utils/common-helpers';

export interface FeatureItem {
    title: string;
    description: string;
    icon: string;
}

interface ServiceFeaturesGridProps {
    features: FeatureItem[];
    variant?: 'default' | 'grey';
}

export function ServiceFeaturesGrid({ features, variant = 'default', }: ServiceFeaturesGridProps) {
    return (
        
        <div className='row relative'>
            <div className='w-full'>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 relative z-10 max-w-7xl mx-auto">
                    {features.map((feature, index) => (
                        <Feature
                            key={feature.title}
                            {...feature}
                            index={index}
                            total={features.length}
                            variant={variant}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

const Feature = ({
    title,
    description,
    icon,
    index,
    total,
    variant,
}: FeatureItem & {
    index: number;
    total: number;
    variant: 'default' | 'grey';
}) => {
    const gradientFrom =
        variant === 'grey'
            ? 'from-[#F5F6F9] dark:from-neutral-800'
            : 'from-neutral-100 dark:from-neutral-800';
    return (
        <div
            className={cn(
                'flex flex-col py-[30px] relative group/feature dark:border-neutral-800',
                index % 3 === 0 && 'lg:border-l',
                (index + 1) % 3 !== 0 && 'lg:border-r',
                index >= 3 && 'lg:border-t',
                index === total - 1 && total % 2 === 0 && 'mb-[-1.2px]',
            )}
        >
            {/* Hover Gradient */}
            <div
                className={cn(
                    'opacity-0 group-hover/feature:opacity-100 transition duration-200 absolute inset-0 h-full w-full pointer-events-none',
                    index < 3
                        ? `bg-gradient-to-t ${gradientFrom} to-transparent`
                        : `bg-gradient-to-b ${gradientFrom} to-transparent`,
                )}
            />

            {/* Icon */}
            <div className="mb-[10px] relative z-10 px-10">
                <Image src={icon} width={36} height={36} alt={title} title={title} />
            </div>

            {/* Title */}
            <div className="text-2xl font-bold mb-[10px] relative z-10 px-[30px]">
                <div className="absolute left-0 inset-y-0 h-6 group-hover/feature:h-8 w-1 rounded-tr-full rounded-br-full bg-neutral-300 dark:bg-neutral-700 group-hover/feature:bg-primary transition-all duration-200" />
                <span className="group-hover/feature:translate-x-2 group-hover/feature:text-primary transition duration-200 inline-block text-neutral-800 dark:text-neutral-100">
                    {title}
                </span>
            </div>

            {/* Description */}
            <p className="mb-0 text-base text-neutral-600 dark:text-neutral-300 relative z-10 px-[30px]">
                {description}
            </p>
        </div>
    );
};
